// Quick offset adjuster -> POST /v1/screens/{id}/offset {deltaMinutes}.
// This is the same operation the POS extension's +/− buttons perform; the
// response is authoritative (the server clamps nothing — out-of-range is a
// 400 whose detail we surface), so no optimistic update.

import { Button, Card, toast } from "@pej/kore/components";
import { useState } from "react";
import { ApiError } from "@/api/error";
import { useOffsetDelta } from "@/api/tuning";
import type { OffsetResponse, Screen } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";

const DELTAS = [-5, -1, 1, 5] as const;

export interface OffsetControlProps {
    screen: Screen;
}

export function OffsetControl({ screen }: OffsetControlProps) {
    const offsetDelta = useOffsetDelta(screen.id);
    const [latest, setLatest] = useState<OffsetResponse | null>(null);

    const offsetMinutes = latest?.offsetMinutes ?? screen.offsetMinutes;
    const displayedMinutes = latest ? latest.displayedMinutes : null;

    async function nudge(deltaMinutes: number) {
        try {
            const result = await offsetDelta.mutateAsync(deltaMinutes);
            setLatest(result);
            // Every write announces itself — two quick taps must never blur
            // into "something changed the offset" (Eric's finding 33).
            toast.success(`Offset is now ${result.offsetMinutes > 0 ? "+" : ""}${result.offsetMinutes} min`);
        } catch (err) {
            if (err instanceof ApiError) {
                toastMutationError("Offset change failed", err);
                return;
            }
            throw err;
        }
    }

    return (
        <Card className="p-6">
            <h2 className="mb-1 text-base font-semibold">Quick offset</h2>
            <p className="mb-4 text-sm text-muted-foreground">
                Nudge the displayed value during service. Takes effect immediately.
            </p>
            <div className="flex items-center gap-4">
                <div className="flex gap-2">
                    {DELTAS.map((delta) => (
                        <Button
                            key={delta}
                            variant="outline"
                            disabled={offsetDelta.isPending}
                            onClick={() => void nudge(delta)}
                        >
                            {delta > 0 ? `+${delta}` : `${delta}`}
                        </Button>
                    ))}
                </div>
                <p className="text-sm" data-testid="offset-status">
                    <span className="text-muted-foreground">Offset </span>
                    <span className="font-medium">{offsetMinutes} min</span>
                    {displayedMinutes !== null && (
                        <>
                            <span className="text-muted-foreground"> · shows now </span>
                            <span className="font-medium">{displayedMinutes} min</span>
                        </>
                    )}
                </p>
            </div>
        </Card>
    );
}
